import { useEffect, useState } from 'react';
import {
  ProxiedSignInPage,
  Progress,
  SignInPage,
  type SignInPageProps,
} from '@backstage/core-components';
import {
  discoveryApiRef,
  oktaAuthApiRef,
  useApi,
} from '@backstage/core-plugin-api';

type SignInMode = 'loading' | 'local' | 'okta';

const PROBE_INTERVAL_MS = 500;
/** yarn start: frontend often wins the race against the auth backend. */
const MAX_PROBE_ATTEMPTS = 60;

function isLocalDevHost(): boolean {
  const host = window.location.hostname;
  return host === 'localhost' || host === '127.0.0.1';
}

async function probeLocalAuth(authBase: string): Promise<'local' | 'okta' | 'retry'> {
  try {
    const response = await fetch(`${authBase}/local/refresh`, {
      credentials: 'include',
    });
    if (response.ok) {
      return 'local';
    }
    if (response.status === 404) {
      return 'okta';
    }
  } catch {
    // backend still starting
  }
  return 'retry';
}

/**
 * Local Mac (localhost): auto sign-in via /api/auth/local/refresh — never Okta.
 * Dev/prod VMs: Okta only (local provider returns 404).
 */
export const AppSignInPage = (props: SignInPageProps) => {
  const discoveryApi = useApi(discoveryApiRef);
  const [mode, setMode] = useState<SignInMode>('loading');

  useEffect(() => {
    let cancelled = false;
    const localMac = isLocalDevHost();

    (async () => {
      const authBase = await discoveryApi.getBaseUrl('auth');

      for (let attempt = 0; attempt < MAX_PROBE_ATTEMPTS; attempt++) {
        if (cancelled) {
          return;
        }
        const result = await probeLocalAuth(authBase);
        if (result === 'local') {
          setMode('local');
          return;
        }
        if (result === 'okta' && !localMac) {
          setMode('okta');
          return;
        }
        await new Promise(resolve => setTimeout(resolve, PROBE_INTERVAL_MS));
      }

      if (!cancelled) {
        setMode(localMac ? 'local' : 'okta');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [discoveryApi]);

  if (mode === 'loading') {
    return <Progress />;
  }

  if (mode === 'local') {
    return <ProxiedSignInPage {...props} provider="local" />;
  }

  return (
    <SignInPage
      {...props}
      auto
      providers={[
        {
          id: 'okta-auth-provider',
          title: 'Okta',
          message: 'Sign in using Okta SSO',
          apiRef: oktaAuthApiRef,
        },
      ]}
    />
  );
};
